import { useRef, useState } from 'react';
import { Modal, Spin, message } from 'antd';
import ModalHeader from '../../../../components/atoms/ModalHeader';
import FormField from '../../../../components/FormField/FormField';
import Button from '../../../../components/atoms/Button';
import FormWrapper from '../../../../components/categories/forms/FormWrapper';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
  useAddOperatorSpecialityMutation,
  useUploadOperatorSpecialityDocumentMutation,
} from '../../../../store/services/lookups';
import { field } from '../../../../components/FormField/interfaces';

const englishRegex = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
const arabicRegex = /^[\u0600-\u06FF0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;

const getAddSubCategorySchema = (t: any) => z.object({
  subCategoryNameEn: z
    .string()
    .min(1, t('categories.validation.subCategoryNameRequired'))
    .regex(englishRegex, t('categories.validation.englishRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  subCategoryNameAr: z
    .string()
    .min(1, t('categories.validation.subCategoryNameRequired'))
    .regex(arabicRegex, t('categories.validation.arabicRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  subCategoryIcon: z
    .any()
    .refine((files) => files && files.length > 0, t('categories.validation.subCategoryImageRequired'))
    .refine(
      (files) => {
        if (!files || files.length === 0) return true;
        const size = files[0]?.originFileObj?.size ?? files[0]?.size;
        return !size || size <= 5 * 1024 * 1024;
      },
      t('categories.validation.maxFileSize5MB')
    ),
});

/**
 * EagerFileField — uploads the file immediately on selection and stores the
 * resulting document ID via onDocumentId.
 */
function EagerFileField({
  name,
  uploadFn,
  onDocumentId,
  onUploadStateChange,
  ...fieldProps
}: Omit<field, 'onChangeFile'> & {
  uploadFn: (formData: FormData) => Promise<any>;
  onDocumentId: (id: string) => void;
  onUploadStateChange?: (isUploading: boolean) => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const { setValue } = useFormContext();

  const handleChange = async (fileList: any[]) => {
    setValue(name, fileList, { shouldValidate: true });

    const file = fileList?.[fileList.length - 1]?.originFileObj;
    if (!file) return;

    setIsUploading(true);
    onUploadStateChange?.(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadFn(formData);
      const docId =
        res?.payload?.[0]?.id ||
        res?.payload?.id ||
        res?.id ||
        '';
      onDocumentId(docId);
    } catch {
      onDocumentId('');
    } finally {
      setIsUploading(false);
      onUploadStateChange?.(false);
    }
  };

  return (
    <Spin spinning={isUploading} size="small">
      <FormField
        {...fieldProps}
        name={name}
        onChangeFile={handleChange}
      />
    </Spin>
  );
}

interface AddSubCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  parentCategoryId: string;
  parentCategoryName: string;
}

export default function AddSubCategoryModal({
  isOpen,
  onClose,
  onSuccess,
  parentCategoryId,
}: AddSubCategoryModalProps) {
  const { t } = useTranslation();

  const [addOperatorSpeciality, { isLoading }] = useAddOperatorSpecialityMutation();
  const [uploadSpecialityDocument] = useUploadOperatorSpecialityDocumentMutation();

  const [isUploadingDocs, setIsUploadingDocs] = useState(false);

  const documentIdRef = useRef<string>('');

  const defaultValues = {
    subCategoryNameEn: '',
    subCategoryNameAr: '',
    subCategoryIcon: [],
  };

  const handleSubmit = async (data: any) => {
    try {
      const payload = {
        nameEn: data.subCategoryNameEn,
        nameAr: data.subCategoryNameAr,
        documentId: documentIdRef.current,
        categoryId: parentCategoryId,
      };

      await addOperatorSpeciality(payload).unwrap();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to add sub-category:', error);
      const errorMessage =
        error?.data?.message ||
        error?.data?.errors?.default ||
        error?.data?.errors?.[0] ||
        t('categories.subCategoryAddFailed');
      message.error(errorMessage);
    }
  };

  const addSubCategorySchema = getAddSubCategorySchema(t);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={false}
      title={
        <ModalHeader
          title={t('categories.addSubCategory')}
          description={t('categories.updateInfo')}
        />
      }
      centered
      width={600}
    >
      <FormWrapper
        schema={addSubCategorySchema}
        onSubmit={handleSubmit}
        values={defaultValues}
      >
        <div className="flex flex-col gap-4 mt-4">
          <FormField
            type="text"
            name="subCategoryNameEn"
            id="subCategoryNameEn"
            label={t('categories.subCategoryEN')}
            placeholder={t('categories.setName')}
            className="!rounded-lg"
          />

          <FormField
            type="text"
            name="subCategoryNameAr"
            id="subCategoryNameAr"
            label={t('categories.subCategoryAR')}
            placeholder={t('categories.setName')}
            className="!rounded-lg"
          />

          <EagerFileField
            type="file"
            name="subCategoryIcon"
            id="subCategoryIcon"
            label={t('categories.subCategoryImage')}
            maxCount={1}
            multiple={false}
            accept="image/png, image/jpeg"
            uploadText={t('categories.dragDropUpload')}
            uploadHints={[
              t('categories.acceptedFileTypes'),
              t('categories.uploadOneFile'),
              t('categories.maxFileSize5MB'),
            ]}
            uploadFn={(fd: FormData) => uploadSpecialityDocument(fd).unwrap()}
            onDocumentId={(id) => { documentIdRef.current = id; }}
            onUploadStateChange={setIsUploadingDocs}
          />
        </div>

        <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} type="button">
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={isLoading} disabled={isUploadingDocs}>
            {t('categories.saveSubCategory')}
          </Button>
        </div>
      </FormWrapper>
    </Modal>
  );
}
